import { useLoggedUser } from '@btime/react-session'
import { generateContext } from '@btime/react-contexts'
import React, { useContext, useEffect, useRef, useState } from 'react'

import { useLoggedUserSignature } from '../../../../utils/hooks/useLoggedUserSignature'
import { applySignatureToState } from './applySignatureToReplies'
import { useFormattedRepliesFormData } from './formatRepliesFormData'

const { StateContext, Provider } = generateContext()
export const useExecutionForm = () => useContext(StateContext)

const formIdentityKey = (questions, replies) =>
  [].concat(questions ?? []).map(q => q?.id).filter(Boolean).join(',') + '/' + (replies?.length ?? 0)

const Form = ({ children, questions, replies, autofillContext }) => {
  const savedSignature = useLoggedUserSignature()
  const loggedUser = useLoggedUser()
  const executionUserContext = React.useMemo(
    () => ({
      savedSignature: savedSignature ?? undefined,
      userName: loggedUser?.name ?? undefined
    }),
    [savedSignature, loggedUser?.name]
  )
  const baseState = useFormattedRepliesFormData(questions, replies, autofillContext)
  const initialState = React.useMemo(
    () => applySignatureToState(baseState, executionUserContext),
    [baseState, executionUserContext]
  )
  const [state, setState] = useState(initialState)
  const activityKey = formIdentityKey(questions, replies)
  const signatureReady = savedSignature != null ? 'ready' : 'pending'
  const hadCategoriesRef = useRef(!!initialState.categories?.length)
  const hadPlaceDataRef = useRef((autofillContext?.placeFieldValues?.length ?? 0) > 0)
  const hadUserDataRef = useRef((autofillContext?.userFieldValues?.length ?? 0) > 0)

  const initialStateRef = useRef(initialState)
  initialStateRef.current = initialState

  useEffect(() => {
    if (activityKey) setState(initialStateRef.current)
  }, [activityKey, signatureReady])

  const placeLen = autofillContext?.placeFieldValues?.length ?? 0
  const userLen = autofillContext?.userFieldValues?.length ?? 0
  const categoriesLength = initialState.categories?.length ?? 0

  useEffect(() => {
    const hasCategories = categoriesLength > 0
    const hasPlaceDataNow = placeLen > 0
    const hasUserDataNow = userLen > 0
    const placeJustArrived = hasPlaceDataNow && !hadPlaceDataRef.current
    const userJustArrived = hasUserDataNow && !hadUserDataRef.current

    if (hasCategories && !hadCategoriesRef.current) {
      setState(initialStateRef.current)
    } else if (hasCategories && (placeJustArrived || userJustArrived)) {
      setState(initialStateRef.current)
    }
    hadCategoriesRef.current = hasCategories
    hadPlaceDataRef.current = hasPlaceDataNow
    hadUserDataRef.current = hasUserDataNow
  }, [categoriesLength, placeLen, userLen])

  return <Provider state={state}>{children}</Provider>
}

export default Form
